import { jsxs, jsx } from "react/jsx-runtime";
import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { AnimateSharedLayout, motion } from "framer-motion";
import { spaceMixin } from "@zillow/constellation";
import { yieldCallback } from "@zillow/yield-callback";
import { event } from "@zillow/universal-analytics";
import CarouselItem from "./CarouselItem.esm.js";
import CarouselDot from "./CarouselDot.esm.js";
import CarouselNavigation from "./CarouselNavigation.esm.js";
import triggerTabClickEvent from "../../utils/clickstream/tab-click.esm.js";
import { wrap } from "../../node_modules/popmotion/dist/es/utils/wrap.esm.js";
const TabsContainer = styled.div.withConfig({
  displayName: "Carousel__TabsContainer",
  componentId: "sc-e82pwv-0"
})(["display:flex;justify-content:center;margin-bottom:", ";flex-wrap:wrap;"], spaceMixin("sm"));
const DotsContainer = styled.div.withConfig({
  displayName: "Carousel__DotsContainer",
  componentId: "sc-e82pwv-1"
})(["display:flex;justify-content:center;flex-wrap:nowrap;margin-top:", ";"], spaceMixin("sm"));
const Slider = styled(motion.div).withConfig({
  displayName: "Carousel__Slider",
  componentId: "sc-e82pwv-2"
})(["width:100%;display:flex;flex-direction:row;"]);
const SlidesDotsAndArrowsContainer = styled.div.withConfig({
  displayName: "Carousel__SlidesDotsAndArrowsContainer",
  componentId: "sc-e82pwv-3"
})(["position:relative;display:flex;flex-direction:row;min-height:300px;overflow:hidden;"]);
const Carousel = ({
  children,
  showTabs,
  showDots,
  showArrows,
  id
}) => {
  const [page, setPage] = useState(0);
  const slideCount = React.Children.count(children);
  const getSlideIndex = (targetPage) => wrap(0, slideCount, targetPage);
  const activeSlideIndex = getSlideIndex(page);
  const triggerClickStreamEvent = (blockDisplayedTxt, targetPage) => event({
    // https://dataportal.zillowgroup.net/event-registry/2048
    envelope: {
      event_type_id: "2048",
      event_type_version_id: "1",
      event_template_id: "126",
      event_template_version_id: "1"
      // Master ID: 2284.2.126.1
    },
    clickstream_trigger: {
      trigger_type_nm: "interaction",
      trigger_location_nm: "cms",
      trigger_source_nm: "cms_carousel",
      trigger_object_nm: "no_trigger_object"
    },
    semantic: {
      semantic_event_nm: "cms_click"
    },
    cms_info: {
      block_id: id || "",
      block_type_cd: "carousel",
      block_displayed_txt: blockDisplayedTxt,
      // We don't want zero-based index, so add 1.
      slide_number: getSlideIndex(targetPage) + 1
    }
  });
  return /* @__PURE__ */ jsxs("div", { children: [
    showTabs && /* @__PURE__ */ jsx(AnimateSharedLayout, { children: /* @__PURE__ */ jsx(TabsContainer, { children: React.Children.map(children, (element, index) => React.cloneElement(element, {
      privateRenderType: "tab",
      // The below props will get passed to <CarouselTab>
      isActive: activeSlideIndex === index,
      onClick: yieldCallback(() => {
        setPage(index);
        triggerTabClickEvent({
          id: element.props.key,
          label: element.props.id,
          isCarouselTab: true
        });
      })
    })) }) }),
    /* @__PURE__ */ jsxs(SlidesDotsAndArrowsContainer, { children: [
      /* @__PURE__ */ jsx(Slider, { animate: {
        x: `${-activeSlideIndex * 100}%`
      }, transition: {
        x: {
          type: "spring",
          stiffness: 300,
          damping: 30
        }
      }, children: React.Children.toArray(children).map((child) => React.cloneElement(child, {
        privateRenderType: "slide",
        showArrows
      })) }),
      showArrows && /* @__PURE__ */ jsx(CarouselNavigation, { onLeftButtonClick: () => setPage((currentPage) => {
        const nextPage = currentPage - 1;
        triggerClickStreamEvent("previous", nextPage);
        return nextPage;
      }), onRightButtonClick: () => setPage((currentPage) => {
        const nextPage = currentPage + 1;
        triggerClickStreamEvent("next", nextPage);
        return nextPage;
      }) })
    ] }),
    showDots && /* @__PURE__ */ jsx(DotsContainer, { children: React.Children.map(children, (_, index) => /* @__PURE__ */ jsx(CarouselDot, { isActive: activeSlideIndex === index, type: "button", onClick: yieldCallback(() => {
      setPage(index);
      triggerClickStreamEvent("jump_dot", index);
    }) }, index.toString())) })
  ] });
};
Carousel.propTypes = {
  /**
   * An array of Carousel content to be displayed
   */
  children: PropTypes.node.isRequired,
  /**
   * Flag to display pagination dots
   */
  showDots: PropTypes.bool,
  /**
   * Flag to display pagination tabs
   */
  showTabs: PropTypes.bool,
  /**
   * Flag to display navigation arrows
   */
  showArrows: PropTypes.bool,
  id: PropTypes.string
};
Carousel.defaultProps = {
  showDots: true,
  showTabs: true,
  showArrows: true,
  id: ""
};
Carousel.Item = CarouselItem;
export {
  DotsContainer,
  SlidesDotsAndArrowsContainer,
  TabsContainer,
  Carousel as default
};
